import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
} from '@mui/material';
import { FiCheck, FiChevronDown } from 'react-icons/fi';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import slugify from 'slugify';
import { getOptionValue } from '@lib/form';
import FieldLabel from '../../FieldLabel';
import getFieldStatus from '../../fieldStatus';

const MENU_ITEM_SX = {
  '&.Mui-selected': { fontWeight: 700 },
};

/**
 * Goes to MUI directly rather than through the `Select` atom, which pins `size="small"` on
 * both the label and the control — that fights the 48px control height the redesign sets in
 * the form theme, and the atom is shared with the vehicle filters.
 */
export default function DropDown(props) {
  const {
    name,
    disabled,
    value = '',
    error = '',
    touched,
    label = '',
    isRequired,
    isMultiple,
    options = [],
    handleBlur,
    handleChange,
    helperText = '',
    readOnly,
  } = props;
  const labelId = useMemo(() => slugify(`label-${name}`), [name]);
  const { hasError, message } = getFieldStatus({ error, touched, helperText });

  const getOptionLabel = (optionValue) => get(
    options.find((o) => getOptionValue(o) === optionValue),
    'label',
  );

  return (
    <FormControl disabled={disabled} error={hasError} fullWidth>
      <InputLabel id={labelId}>
        <FieldLabel isRequired={isRequired} label={label} />
      </InputLabel>
      <Select
        IconComponent={FiChevronDown}
        // `aria-required` rather than MUI's `required`, which would print an asterisk.
        inputProps={{ 'aria-required': isRequired || undefined }}
        label={<FieldLabel isRequired={isRequired} label={label} />}
        labelId={labelId}
        multiple={isMultiple}
        name={name}
        onBlur={handleBlur}
        onChange={(event) => handleChange(event.target.value)}
        readOnly={readOnly}
        renderValue={
          isMultiple
            ? (selected) => selected.map(getOptionLabel).filter((x) => x).join(', ')
            : getOptionLabel
        }
        sx={{
          // MUI positions its own caret for the built-in triangle icon; the outline chevron
          // is a different shape and needs its own inset.
          '& .MuiSelect-icon': { right: 14, fontSize: 20 },
        }}
        value={isMultiple && !Array.isArray(value) ? [] : value}
      >
        {options?.map((item) => {
          const itemValue = getOptionValue(item);
          const isSelected = isMultiple
            ? (value ?? []).indexOf(itemValue) > -1
            : value === itemValue;

          return (
            <MenuItem
              key={item.id}
              disabled={Boolean(item.isDisabled)}
              sx={MENU_ITEM_SX}
              value={itemValue}
            >
              <ListItemText primary={item.label} />
              {isSelected && (
                <Box
                  aria-hidden
                  sx={{ display: 'flex', color: 'primary.main', ml: 1 }}
                >
                  <FiCheck size={16} strokeWidth={3} />
                </Box>
              )}
            </MenuItem>
          );
        })}
      </Select>
      {message && (
        <FormHelperText>
          {message}
        </FormHelperText>
      )}
    </FormControl>
  );
}

DropDown.propTypes = {
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  error: PropTypes.string,
  touched: PropTypes.bool,
  label: PropTypes.string,
  isRequired: PropTypes.bool,
  isMultiple: PropTypes.bool,
  options: PropTypes.array,
  handleBlur: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  helperText: PropTypes.string,
  readOnly: PropTypes.bool,
};
